import * as React from "react";
import { useState } from "react";
import QuestionEditor, { ExtSurveyQuestion } from "./QuestionEditor";
import {
  IconPlus,
  SecondaryBlueButton,
  Survey,
  SurveyQuestion,
  TertiaryBlueButton
} from "@velaro/velaro-component-lib";
import IconChevronDown from "@/icons/IconChevronDown";
import IconChevronUp from "@/icons/IconChevronUp";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

const CONSENT_TEMPLATES: Record<string, { label: string; version: string; text: string }> = {
  gdpr: {
    label: "GDPR (EU)",
    version: "2026.1",
    text: `<p>By submitting this form, I consent to <strong>[Company Name]</strong> collecting and processing my personal data for the purpose of providing customer support services. My data will be processed lawfully and transparently in compliance with Regulation (EU) 2016/679 (GDPR).</p><p>I understand I have the right to: access my data, correct inaccuracies, request erasure, restrict or object to processing, and data portability. I may withdraw this consent at any time without affecting prior processing. To exercise my rights or for questions, contact our Data Protection Officer at <strong>[dpo@company.com]</strong>.</p><p><em>⚠️ This template is for reference only. Review with your legal counsel before deployment.</em></p>`
  },
  ccpa: {
    label: "CCPA / CPRA (California)",
    version: "2026.1",
    text: `<p>By submitting this form, you acknowledge that <strong>[Company Name]</strong> collects personal information including name, email, and contact details for customer support purposes.</p><p>Under the California Consumer Privacy Act (CCPA) as amended by the California Privacy Rights Act (CPRA), you have the right to: know what personal information we collect and how it is used, request deletion of your personal information, correct inaccurate personal information, opt out of the sale or sharing of your personal information, and limit the use of sensitive personal information. We do not sell personal information. To exercise your rights, contact us at <strong>[privacy@company.com]</strong> or visit our <a href="#">Privacy Page</a>.</p><p><em>⚠️ This template is for reference only. Review with your legal counsel before deployment.</em></p>`
  },
  lgpd: {
    label: "LGPD (Brazil)",
    version: "2026.1",
    text: `<p>Ao enviar este formulário, você consente com o tratamento dos seus dados pessoais pela <strong>[Nome da Empresa]</strong> para fins de suporte ao cliente, em conformidade com a Lei Geral de Proteção de Dados Pessoais (LGPD — Lei nº 13.709/2018).</p><p>Você tem os seguintes direitos: confirmação e acesso aos seus dados, correção de dados incompletos ou inexatos, anonimização, bloqueio ou eliminação de dados desnecessários, portabilidade, eliminação de dados tratados com consentimento e revogação do consentimento. Para exercer seus direitos, entre em contato com nosso Encarregado de Proteção de Dados em <strong>[encarregado@empresa.com]</strong>.</p><p><em>⚠️ Este texto é apenas para referência. Revise com sua equipe jurídica antes de usar.</em></p>`
  },
  hipaa: {
    label: "HIPAA (US Healthcare)",
    version: "2026.1",
    text: `<p>By submitting this form, you authorize <strong>[Company Name]</strong> to collect and use information provided for the purpose of providing customer support related to healthcare services. This authorization is made pursuant to the Health Insurance Portability and Accountability Act (HIPAA) of 1996.</p><p>You understand that: (1) you have the right to revoke this authorization in writing at any time; (2) revocation does not apply to actions already taken in reliance on this authorization; (3) your treatment, payment, enrollment, or eligibility for benefits is not conditioned on signing this authorization. For questions, contact our Privacy Officer at <strong>[privacy@company.com]</strong>.</p><p><em>⚠️ If you are a HIPAA-covered entity or business associate, consult legal counsel before deploying. This template is for reference only.</em></p>`
  },
  coppa: {
    label: "COPPA (US Children's Privacy)",
    version: "2026.1",
    text: `<p>This form is intended for users <strong>13 years of age and older</strong>. <strong>[Company Name]</strong> does not knowingly collect personal information from children under 13 years of age.</p><p>If you are a parent or guardian and believe your child has submitted personal information through this form, please contact us immediately at <strong>[privacy@company.com]</strong> so we can delete it. By submitting this form, you represent that you are 13 years of age or older. Our collection and use of personal information complies with the Children's Online Privacy Protection Act (COPPA).</p><p><em>⚠️ This template is for reference only. Review with your legal counsel before deployment.</em></p>`
  },
  pci: {
    label: "PCI DSS (Payment Card)",
    version: "2026.1",
    text: `<p>By engaging in this chat, you agree <strong>not</strong> to share payment card numbers, CVV codes, PINs, or other sensitive payment information in the chat session. <strong>[Company Name]</strong> is committed to protecting payment card data in compliance with the Payment Card Industry Data Security Standard (PCI DSS).</p><p>This chat may be monitored and recorded for security and compliance purposes. For payment-related inquiries, our team will direct you to a secure, PCI-compliant payment channel.</p><p><em>⚠️ This template is for reference only. Review with your legal counsel and QSA before deployment.</em></p>`
  },
  general: {
    label: "General / Custom",
    version: "2026.1",
    text: `<p>By submitting this form, you agree to <strong>[Company Name]</strong>'s <a href="#">Terms of Service</a> and <a href="#">Privacy Policy</a>. We collect the information provided to respond to your inquiry, improve our services, and communicate with you about your support request.</p><p>Your information will be handled in accordance with our Privacy Policy and applicable privacy laws. For questions about how we use your data, contact us at <strong>[privacy@company.com]</strong>.</p><p><em>⚠️ This template is for reference only. Review with your legal counsel before deployment.</em></p>`
  }
};

interface ExtSurvey extends Survey {
  formHeading?: string;
  formSubheading?: string;
  formPurpose?: string;
  consentEnabled?: boolean;
  consentType?: string;
  consentText?: string;
  consentRegulation?: string;
  consentTemplateKey?: string;
  consentTemplateVersion?: string;
  consentAutoUpdate?: boolean;
}

interface Props {
  survey: Survey;
  errors: Record<string, string>[];
  netSuiteMappings?: any[];
  loadingMappings?: boolean;
  update(survey: Survey): void;
  remove(): void;
}

export default function SurveyEditor({
  survey,
  errors,
  netSuiteMappings = [],
  loadingMappings = false,
  update,
  remove
}: Props) {
  const ext = survey as ExtSurvey;
  const [showVariableHelp, setShowVariableHelp] = useState(false);
  const [showFormHeader, setShowFormHeader] = useState(
    !!(ext.formHeading || ext.formSubheading || ext.formPurpose)
  );
  const [showConsent, setShowConsent] = useState(!!ext.consentEnabled);
  const [selectedTemplate, setSelectedTemplate] = useState<string>("");
  const [templateOriginalText, setTemplateOriginalText] = useState<string>("");
  const questions = survey.questions as ExtSurveyQuestion[];

  function updateExt(patch: Partial<ExtSurvey>) {
    update({ ...survey, ...patch } as Survey);
  }

  function addQuestion() {
    update({
      ...survey,
      questions: [
        ...survey.questions,
        {
          type: "name",
          text: "What is your name?",
          required: true,
          variable: "name",
          options: [],
          crmOptions: {}
        } as SurveyQuestion
      ]
    });
  }

  function updateQuestion(index: number, question: ExtSurveyQuestion) {
    if (question.type === "label" || (question.type as string) === "pageBreak") {
      question.options = [];
      question.required = false;
      question.variable = undefined;
    }
    if ((question.type as string) === "hidden") {
      question.options = [];
      question.required = false;
      // DO NOT clear variable — hidden fields store captured data under this variable name
    }
    const clone = [...survey.questions] as ExtSurveyQuestion[];
    clone[index] = question;
    update({ ...survey, questions: clone });
  }

  function moveQuestion(from: number, to: number) {
    if (to < 0 || to >= survey.questions.length) return;
    const clone = [...survey.questions] as ExtSurveyQuestion[];
    const [item] = clone.splice(from, 1);
    clone.splice(to, 0, item);
    update({ ...survey, questions: clone });
  }

  function removeQuestion(index: number) {
    const clone = [...survey.questions];
    clone.splice(index, 1);
    update({ ...survey, questions: clone });
  }

  const hasQuestions = survey.questions.length > 0;

  return (
    <div className="flex flex-col gap-3">
      {/* Survey name row */}
      <div className="flex items-center gap-2">
        <input
          type="text"
          className="input flex-1 min-w-0"
          required
          placeholder="Survey name..."
          value={survey.name}
          onChange={(e) => update({ ...survey, name: e.currentTarget.value })}
        />
        <TertiaryBlueButton label="Delete survey" onClick={remove} />
      </div>

      {/* Variable help collapsible */}
      <button
        type="button"
        className="flex items-center gap-1.5 text-sm text-blue-500 hover:text-blue-600 w-fit"
        onClick={() => setShowVariableHelp((v) => !v)}
      >
        {showVariableHelp ? <IconChevronUp /> : <IconChevronDown />}
        <span>How do survey fields pass data to rules &amp; workflows?</span>
      </button>

      {showVariableHelp && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-slate-700 leading-relaxed">
          <p className="font-semibold mb-2 text-blue-800">
            Each field stores its answer as a variable
          </p>
          <p className="mb-3">
            When a visitor submits the survey, every field&apos;s answer is saved
            under its{" "}
            <span className="font-semibold">Variable name</span>. Reference it
            anywhere using{" "}
            <code className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-mono text-xs">
              {"<<variable_name>>"}
            </code>
            .
          </p>
          <p className="font-semibold mb-1 text-blue-800">Where to use these variables:</p>
          <ul className="space-y-1.5 ml-4">
            <li className="flex gap-2">
              <span className="text-blue-400 shrink-0 mt-0.5">→</span>
              <span>
                <span className="font-semibold">Rule conditions</span> — add an
                &ldquo;Attribute&rdquo; condition, match the variable name to route
                chats or assign agents
              </span>
            </li>
            <li className="flex gap-2">
              <span className="text-blue-400 shrink-0 mt-0.5">→</span>
              <span>
                <span className="font-semibold">Workflow Send Message nodes</span> —
                personalize messages with{" "}
                <code className="bg-blue-100 text-blue-700 px-1 rounded font-mono text-xs">
                  {"<<variable_name>>"}
                </code>
              </span>
            </li>
            <li className="flex gap-2">
              <span className="text-blue-400 shrink-0 mt-0.5">→</span>
              <span>
                <span className="font-semibold">Skip logic</span> — show or hide
                later questions based on earlier answers (use the{" "}
                <em>Show only when...</em> toggle on each question)
              </span>
            </li>
          </ul>
          <p className="mt-3 text-xs text-slate-500">
            Tip: Fields like{" "}
            <code className="font-mono text-xs">name</code>,{" "}
            <code className="font-mono text-xs">email</code>, and{" "}
            <code className="font-mono text-xs">language</code>{" "}
            have their variable names set automatically.
          </p>
        </div>
      )}

      {/* Form Header section */}
      <div className="border border-slate-200 rounded-xl overflow-hidden">
        <button
          type="button"
          className="w-full flex items-center gap-2 px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
          onClick={() => setShowFormHeader((v) => !v)}
          aria-expanded={showFormHeader}
        >
          {showFormHeader ? <IconChevronUp /> : <IconChevronDown />}
          <span className="text-sm font-medium text-slate-700">Form Header</span>
          <span className="text-xs text-slate-400 ml-1">optional — shown above questions in visitor-facing surveys</span>
        </button>
        {showFormHeader && (
          <div className="px-4 py-3 space-y-3 bg-white">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">
                Heading
              </label>
              <input
                type="text"
                className="input w-full"
                placeholder="e.g. &quot;Get in touch&quot; or &quot;We're here to help&quot;"
                value={ext.formHeading ?? ""}
                onChange={(e) => updateExt({ formHeading: e.currentTarget.value })}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">
                Subheading
              </label>
              <input
                type="text"
                className="input w-full"
                placeholder='e.g. "How may we help you today?"'
                value={ext.formSubheading ?? ""}
                onChange={(e) => updateExt({ formSubheading: e.currentTarget.value })}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">
                Purpose
              </label>
              <input
                type="text"
                className="input w-full"
                placeholder='e.g. "Please fill in the details below so we can connect you with the right team"'
                value={ext.formPurpose ?? ""}
                onChange={(e) => updateExt({ formPurpose: e.currentTarget.value })}
              />
            </div>
          </div>
        )}
      </div>

      {/* Question cards */}
      {!hasQuestions && (
        <div className="py-8 text-center text-slate-400 border-2 border-dashed border-slate-200 rounded-xl">
          <p className="text-sm">This survey has no questions yet.</p>
          <p className="text-xs mt-1">Click &ldquo;Add Question&rdquo; below to get started.</p>
        </div>
      )}

      {hasQuestions && (
        <div className="flex flex-col gap-3">
          {questions.map((q, i) => (
            <QuestionEditor
              key={i}
              index={i}
              allQuestions={questions}
              errors={errors[i] || {}}
              question={q}
              netSuiteMappings={netSuiteMappings}
              loadingMappings={loadingMappings}
              isFirst={i === 0}
              isLast={i === questions.length - 1}
              update={(q) => updateQuestion(i, q)}
              remove={() => removeQuestion(i)}
              moveUp={() => moveQuestion(i, i - 1)}
              moveDown={() => moveQuestion(i, i + 1)}
            />
          ))}
        </div>
      )}

      {/* Privacy Consent section */}
      <div className="border border-slate-200 rounded-xl overflow-hidden">
        <button
          type="button"
          className="w-full flex items-center gap-2 px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
          onClick={() => setShowConsent((v) => !v)}
          aria-expanded={showConsent}
        >
          {showConsent ? <IconChevronUp /> : <IconChevronDown />}
          <span className="text-sm font-medium text-slate-700">Privacy Consent</span>
        </button>
        {showConsent && (
          <div className="px-4 py-3 space-y-3 bg-white">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <div
                onClick={() => updateExt({ consentEnabled: !ext.consentEnabled })}
                role="checkbox"
                aria-checked={!!ext.consentEnabled}
                aria-label="Enable consent block on this survey"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === " " || e.key === "Enter")
                    updateExt({ consentEnabled: !ext.consentEnabled });
                }}
                className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${
                  ext.consentEnabled ? "bg-blue-500" : "bg-slate-200"
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                    ext.consentEnabled ? "translate-x-4" : "translate-x-0"
                  }`}
                />
              </div>
              <span className="text-xs font-medium text-slate-600">Enable consent block on this survey</span>
            </label>
            {ext.consentEnabled && (
              <>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">
                    Type
                  </label>
                  <select
                    className="input w-full"
                    aria-label="Consent type"
                    value={ext.consentType ?? "notice"}
                    onChange={(e) => updateExt({ consentType: e.currentTarget.value })}
                  >
                    <option value="notice">Notice (informational)</option>
                    <option value="explicit">Explicit (checkbox required)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">
                    Regulation
                  </label>
                  <select
                    className="input w-full"
                    aria-label="Consent regulation"
                    value={ext.consentRegulation ?? "gdpr"}
                    onChange={(e) => updateExt({ consentRegulation: e.currentTarget.value })}
                  >
                    <option value="gdpr">GDPR</option>
                    <option value="ccpa">CCPA</option>
                    <option value="lgpd">LGPD</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
                <div className="text-xs text-blue-500 mt-1">
                  💡 Tip: You also have a site-wide consent message in{" "}
                  <a href="/messaging/site-settings" className="underline">Site Settings → Privacy</a>.
                  Per-survey consent takes precedence over the site-wide setting for this survey.
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">
                    Consent text
                  </label>
                  <div className="mb-2 flex items-center gap-2">
                    <select
                      className="input flex-1"
                      aria-label="Select a consent template"
                      value={selectedTemplate}
                      onChange={(e) => {
                        const templateKey = e.currentTarget.value;
                        if (!templateKey) {
                          setSelectedTemplate("");
                          setTemplateOriginalText("");
                          return;
                        }
                        const tpl = CONSENT_TEMPLATES[templateKey];
                        setSelectedTemplate(templateKey);
                        setTemplateOriginalText(tpl.text);
                        updateExt({
                          consentText: tpl.text,
                          consentTemplateKey: templateKey,
                          consentTemplateVersion: tpl.version,
                          consentAutoUpdate: ext.consentAutoUpdate ?? false,
                        });
                      }}
                    >
                      <option value="">Select a template to pre-fill...</option>
                      {Object.entries(CONSENT_TEMPLATES).map(([key, tpl]) => (
                        <option key={key} value={key}>{tpl.label}</option>
                      ))}
                    </select>
                    {selectedTemplate && ext.consentText !== templateOriginalText && (
                      <button
                        type="button"
                        className="text-xs text-blue-500 hover:text-blue-700 underline whitespace-nowrap"
                        onClick={() => updateExt({ consentText: templateOriginalText })}
                      >
                        Restore original template
                      </button>
                    )}
                  </div>
                  <CKEditor
                    editor={ClassicEditor as any}
                    config={{
                      licenseKey: "GPL",
                      toolbar: ["bold", "italic", "link"],
                      link: {
                        addTargetToExternalLinks: true,
                        defaultProtocol: "https://"
                      }
                    }}
                    data={ext.consentText ?? ""}
                    onChange={(_event: any, editor: { getData: () => string }) => {
                      updateExt({ consentText: editor.getData() });
                    }}
                  />
                  <style>{`
                    .ck.ck-editor__main > .ck-editor__editable:not(.ck-focused) { border-color: #d1d5db; }
                    .ck.ck-balloon-panel { z-index: 10000 !important; }
                    .ck-powered-by { display: none !important; }
                    .ck-editor__top .ck-sticky-panel .ck-toolbar,
                    .ck-editor__top .ck-sticky-panel__content,
                    .ck.ck-toolbar { border-top-left-radius: 12px !important; border-top-right-radius: 12px !important; }
                    .ck.ck-editor__main > .ck-editor__editable { border-bottom-left-radius: 12px !important; border-bottom-right-radius: 12px !important; }
                    .ck-content { font-size: 14px !important; line-height: 1.5 !important; }
                  `}</style>
                  <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800 mt-2">
                    <svg className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <span>
                      <strong>Legal review required.</strong> These templates reflect 2026 best practices but are not legal advice.
                      Replace <strong>[Company Name]</strong>, <strong>[privacy@company.com]</strong>, and all bracketed placeholders before going live.
                      Have your legal team review before deployment.
                    </span>
                  </div>
                </div>
                {ext.consentEnabled && ext.consentTemplateKey && (
                  <div className="border border-blue-100 rounded-lg p-3 bg-blue-50">
                    <label className="flex items-start gap-2 cursor-pointer select-none">
                      <div
                        onClick={() => updateExt({ consentAutoUpdate: !ext.consentAutoUpdate })}
                        role="checkbox"
                        aria-checked={!!ext.consentAutoUpdate}
                        aria-label="Auto-update consent language when Velaro releases new regulatory templates"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === " " || e.key === "Enter")
                            updateExt({ consentAutoUpdate: !ext.consentAutoUpdate });
                        }}
                        className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer shrink-0 mt-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${
                          ext.consentAutoUpdate ? "bg-blue-500" : "bg-slate-200"
                        }`}
                      >
                        <span
                          className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                            ext.consentAutoUpdate ? "translate-x-4" : "translate-x-0"
                          }`}
                        />
                      </div>
                      <div>
                        <div className="text-xs font-medium text-blue-800">
                          Auto-update when Velaro releases new regulatory templates
                        </div>
                        <div className="text-xs text-blue-600 mt-0.5">
                          When enabled, Velaro may automatically update this consent text when new{" "}
                          <strong>{ext.consentTemplateKey?.toUpperCase()}</strong>{" "}
                          template versions are released. You'll be notified by email before changes take effect.
                          This does not constitute legal advice — review all updates before publishing.
                        </div>
                        {ext.consentAutoUpdate && (
                          <div className="text-[11px] text-blue-500 mt-1 italic">
                            ✓ Opted in — using template <strong>{ext.consentTemplateKey}</strong> v{ext.consentTemplateVersion ?? "2026.1"}
                          </div>
                        )}
                      </div>
                    </label>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <div>
        <SecondaryBlueButton
          prefixIcon={<IconPlus />}
          label="Add Question"
          onClick={addQuestion}
        />
      </div>
    </div>
  );
}
